import { useEffect, useState, FormEvent } from 'react';
import { AppShell } from '../components/AppShell';
import { Modal } from '../components/Modal';
import { useConfirm } from '../components/ConfirmProvider';
import { useUserOrg } from '../lib/org';
import { LoadingIndicator } from '../components/LoadingIndicator';
import {
  listTrainingPeople,
  listAllTrainingRecords,
  updateTrainingPerson,
  archiveTrainingPerson,
  upsertTrainingRecord,
  deleteTrainingRecord,
  type TrainingPerson,
  type TrainingRecord,
} from '../lib/db';
import { cacheListByOrg } from '../lib/cache';

interface CardSpec { key: string; label: string; }

const CARDS: CardSpec[] = [
  { key: 'cscs',             label: 'CSCS / equivalent' },
  { key: 'first_aid',        label: 'First Aid (3yr)' },
  { key: 'asbestos',         label: 'Asbestos awareness' },
  { key: 'pasma',            label: 'PASMA' },
  { key: 'ipaf',             label: 'IPAF MEWP' },
  { key: 'cpcs_telehandler', label: 'CPCS Telehandler' },
  { key: 'abrasive_wheels',  label: 'Abrasive Wheels' },
];

interface CardDraft {
  has: boolean;
  expires_on: string;
}

interface Draft {
  full_name: string;
  notes: string;
  cards: Record<string, CardDraft>;
}

function emptyCardMap(): Record<string, CardDraft> {
  const out: Record<string, CardDraft> = {};
  for (const c of CARDS) out[c.key] = { has: false, expires_on: '' };
  return out;
}

function expiringCount(records: TrainingRecord[]): number {

  const soon = new Date(); soon.setDate(soon.getDate() + 30);
  return records.filter((r) => r.expires_on && new Date(r.expires_on) <= soon).length;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

export function RegisterTraining() {
  const { org } = useUserOrg();
  const { confirm } = useConfirm();
  const [people, setPeople] = useState<TrainingPerson[]>([]);
  const [records, setRecords] = useState<TrainingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<TrainingPerson | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft] = useState<Draft>({ full_name: '', notes: '', cards: emptyCardMap() });
  const [submitting, setSubmitting] = useState(false);

  async function refresh() {
    if (!org) return;
    setLoading(true);
    try {
      const [ppl, recs] = await Promise.all([
        listTrainingPeople(org.org_id),
        listAllTrainingRecords(org.org_id),
      ]);
      setPeople(ppl);
      setRecords(recs);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Initial load — stale-while-revalidate. Post-mutation refreshes
  // still call refresh() directly for network-only behaviour. Records
  // and people are both cached so the table can render instantly.
  useEffect(() => {
    if (!org) return;
    let cancelled = false;
    let haveCache = false;
    Promise.all([
      cacheListByOrg<TrainingPerson>('training', org.org_id),
      // No dedicated cache store for records — we'll just refresh from
      // network. People is the bigger UI win since it drives the rows.
    ]).then(([cachedPeople]) => {
      if (cancelled) return;
      if (cachedPeople.length > 0) {
        haveCache = true;
        setPeople(cachedPeople);
        setLoading(false);
      }
    }).catch(() => {});
    Promise.all([
      listTrainingPeople(org.org_id),
      listAllTrainingRecords(org.org_id),
    ]).then(([ppl, recs]) => {
      if (cancelled) return;
      setPeople(ppl);
      setRecords(recs);
      setError(null);
    }).catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled && !haveCache) setLoading(false); });
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [org]);

  function startEdit(p: TrainingPerson) {
    setEditing(p);
    const myRecs = records.filter((r) => r.person_id === p.id);
    const cardMap = emptyCardMap();
    for (const r of myRecs) {
      if (cardMap[r.card_type]) {
        cardMap[r.card_type] = { has: true, expires_on: r.expires_on ?? '' };
      }
    }
    setDraft({ full_name: p.full_name, notes: p.notes ?? '', cards: cardMap });
    setShowModal(true);
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!org) return;
    if (!draft.full_name.trim()) { setError('Name is required.'); return; }
    setSubmitting(true);
    try {
      if (!editing) return;
      await updateTrainingPerson(editing.id, {
        full_name: draft.full_name.trim(),
        notes: draft.notes.trim() || null,
      });
      const personId = editing.id;

      // Reconcile each card
      for (const c of CARDS) {
        const d = draft.cards[c.key];
        if (d.has) {
          await upsertTrainingRecord({
            orgId: org.org_id,
            person_id: personId,
            card_type: c.key,
            expires_on: d.expires_on || null,
          });
        } else {
          // If not held, delete any existing record
          await deleteTrainingRecord(personId, c.key).catch(() => null);
        }
      }

      setShowModal(false);
      setEditing(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function onArchive(p: TrainingPerson) {
    if (!(await confirm({
      title: `Archive ${p.full_name}?`,
      body: 'They\'ll be removed from the matrix. Their existing card records stay on file.',
      confirmLabel: 'Archive',
      danger: true,
    }))) return;
    try {
      await archiveTrainingPerson(p.id);
      setShowModal(false);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <AppShell title="Training matrix" back="/registers">
      {error && <div className="banner banner-error">{error}</div>}

      {loading ? (
        <LoadingIndicator />
      ) : people.length === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ margin: '0 0 6px', fontWeight: 600 }}>No people on your training matrix yet.</p>
          <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 14 }}>People appear here when you induct them on a site. Tap a name to record their cards and expiry dates.</p>
        </div>
      ) : (
        <ul className="person-list">
          {people.map((p) => {
            const myRecs = records.filter((r) => r.person_id === p.id);
            const exp = expiringCount(myRecs);
            return (
              <li key={p.id}>
                <button type="button" className="site-item-link" onClick={() => startEdit(p)} style={{ width: '100%', textAlign: 'left', font: 'inherit' }}>
                  <div>
                    <p className="site-name">
                      {p.full_name}
                      {exp > 0 && <span className="badge badge-warn" style={{ marginLeft: 8 }}>{exp} expiring</span>}
                    </p>
                    <p className="site-meta">
                      {myRecs.length} card{myRecs.length === 1 ? '' : 's'} on file
                    </p>
                  </div>
                  <span className="chev">›</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <Modal open={showModal} title="Edit training records" onClose={() => { setShowModal(false); setEditing(null); }}>
        <form onSubmit={onSave}>
          <div className="field"><label htmlFor="training-name">Full name *</label><input id="training-name" type="text" required value={draft.full_name} onChange={(e) => setDraft({ ...draft, full_name: e.target.value })} autoFocus /></div>

          {CARDS.map((c) => (
            <div key={c.key} className="card" style={{ padding: 12, marginBottom: 8 }}>
              <label className="check-row" style={{ padding: 0, border: 'none' }}>
                <input
                  type="checkbox"
                  checked={draft.cards[c.key].has}
                  onChange={(e) => setDraft({
                    ...draft,
                    cards: {
                      ...draft.cards,
                      [c.key]: { ...draft.cards[c.key], has: e.target.checked },
                    },
                  })}
                />
                <span style={{ fontWeight: 600 }}>{c.label}</span>
              </label>
              {draft.cards[c.key].has && (
                <div className="field" style={{ margin: '8px 0 0' }}>
                  <label htmlFor={`training-${c.key}-expires`} style={{ fontSize: 11 }}>Expires</label>
                  <input
                    id={`training-${c.key}-expires`}
                    type="date"
                    value={draft.cards[c.key].expires_on}
                    onChange={(e) => setDraft({
                      ...draft,
                      cards: {
                        ...draft.cards,
                        [c.key]: { ...draft.cards[c.key], expires_on: e.target.value },
                      },
                    })}
                  />
                </div>
              )}
            </div>
          ))}

          <div className="field"><label htmlFor="training-notes">Notes</label><textarea id="training-notes" rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></div>

          <div className="btn-stack">
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</button>
            {editing && <button type="button" className="btn btn-ghost" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => onArchive(editing)}>Archive</button>}
            <button type="button" className="btn btn-ghost" onClick={() => { setShowModal(false); setEditing(null); }}>Cancel</button>
          </div>
        </form>
      </Modal>
    </AppShell>
  );
}

// Suppress unused import warning when fmtDate is only used inline in JSX above.
// (TypeScript's noUnusedLocals fires on top-level imports we kept for clarity.)
export const _useFmtDate = fmtDate;
